
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getFCMToken, deleteFCMToken } from './firebase.service';

const isNative = () => Capacitor.isNativePlatform();
const FCM_TOKEN_KEY = 'triad_fcm_token';
const WEB_FCM_TOKEN_KEY = 'triad_web_fcm_token';
let nativeListenersReady = false;

const waitForToken = (timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      const err = new Error('FCM registration timed out (google-services.json ausente?)');
      err.code = 'FCM_UNAVAILABLE';
      reject(err);
    }, timeoutMs);
    let regListener = null;
    let errListener = null;
    const cleanup = async () => {
      clearTimeout(timer);
      try { regListener && await regListener.remove(); } catch (e) { /* listener já removido */ }
      try { errListener && await errListener.remove(); } catch (e) { /* listener já removido */ }
    };
    regListener = PushNotifications.addListener('registration', async (token) => {
      await cleanup();
      resolve(token.value);
    });
    errListener = PushNotifications.addListener('registrationError', async (err) => {
      await cleanup();
      const error = new Error('FCM registration failed: ' + ((err && err.error) || 'unknown'));
      error.code = 'FCM_UNAVAILABLE';
      reject(error);
    });
    PushNotifications.register().catch(async (e) => {
      await cleanup();
      const error = new Error('FCM register call failed');
      error.code = 'FCM_UNAVAILABLE';
      error.cause = e;
      reject(error);
    });
  });
};

export const pushService = {
  isSupported() {
    // No nativo, push é via FCM (requer google-services.json no app
    // e endpoint de device-token no backend — ver docs).
    if (isNative()) return true;
    // Web via FCM: precisa de Notification + ServiceWorker (background).
    // PushManager/WebPush desativado.
    return 'serviceWorker' in navigator && 'Notification' in window;
  },

  isNative,

  // Registra listeners de push nativo uma única vez (foreground -> callback).
  ensureNativeListeners(onNotification) {
    if (!isNative() || nativeListenersReady) return;
    nativeListenersReady = true;
    try {
      PushNotifications.addListener('registration', async (token) => {
        try {
          if (token && token.value) {
            localStorage.setItem(FCM_TOKEN_KEY, token.value);
            // Reforça o vínculo token<->usuário quando trocar.
            await fetch('/api/notifications/device-token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: token.value, platform: Capacitor.getPlatform() })
            }).catch(() => {});
          }
        } catch (e) { /* intencionalmente silencioso */ }
      });
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        try {
          if (typeof onNotification === 'function') onNotification(notification);
        } catch (e) { /* intencionalmente silencioso */ }
      });
    } catch (e) {
      console.warn('Native push listeners unavailable:', e);
    }
  },

  async getVapidKey() {
    try {
      const response = await fetch('/api/notifications/vapid-key', {
        headers: {}
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('Error fetching VAPID key:', error);
      return null;
    }
  },

  async getSubscription() {
    if (isNative()) {
      try {
        const perm = await PushNotifications.checkPermissions();
        if (!perm || perm.receive !== 'granted') return null;
        const cached = localStorage.getItem(FCM_TOKEN_KEY);
        return cached ? { endpoint: cached } : null;
      } catch (e) {
        return null;
      }
    }
    // Web: FCM (WebPush/PushManager desativado — ver firebase.service).
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') return null;
      const cached = localStorage.getItem(WEB_FCM_TOKEN_KEY);
      if (cached) return { endpoint: cached };
      const token = await getFCMToken();
      localStorage.setItem(WEB_FCM_TOKEN_KEY, token);
      return { endpoint: token };
    } catch (e) {
      return null;
    }
  },

  async requestPermission() {
    if (isNative()) {
      const perm = await PushNotifications.requestPermissions();
      if (!perm || perm.receive !== 'granted') {
        throw new Error('Notification permission not granted');
      }
      return 'granted';
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }
    return permission;
  },

  async subscribeNative() {
    await this.requestPermission();
    const token = await waitForToken();
    localStorage.setItem(FCM_TOKEN_KEY, token);
    // Endpoint a ser implementado no backend (proxy p/ Go). Sem ele, o toggle
    // reverte com erro explicativo em vez de fingir sucesso.
    const response = await fetch('/api/notifications/device-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform: Capacitor.getPlatform() })
    });
    if (!response.ok) {
      const err = new Error('Backend sem suporte a device-token FCM ainda');
      err.code = 'BACKEND_MISSING';
      throw err;
    }
    return { endpoint: token };
  },

  async subscribe() {
    if (isNative()) return this.subscribeNative();

    // Web via FCM (WebPush desativado).
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }
    const token = await getFCMToken();
    localStorage.setItem(WEB_FCM_TOKEN_KEY, token);
    const response = await fetch('/api/notifications/device-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform: 'web' })
    });
    if (!response.ok) {
      localStorage.removeItem(WEB_FCM_TOKEN_KEY);
      throw new Error('Backend sem suporte a device-token FCM ainda');
    }
    return { endpoint: token };
  },

  async unsubscribeNative() {
    try {
      await PushNotifications.unregister().catch(() => {});
    } catch (e) { /* intencionalmente silencioso */ }
    const cached = localStorage.getItem(FCM_TOKEN_KEY);
    localStorage.removeItem(FCM_TOKEN_KEY);
    if (cached) {
      try {
        await fetch('/api/notifications/device-token', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: cached })
        });
      } catch (e) { /* intencionalmente silencioso */ }
    }
  },

  async unsubscribe() {
    if (isNative()) return this.unsubscribeNative();

    const cached = localStorage.getItem(WEB_FCM_TOKEN_KEY);
    localStorage.removeItem(WEB_FCM_TOKEN_KEY);
    try {
      await deleteFCMToken();
    } catch (e) { /* sem SW/FCM: segue degradado */ }
    if (cached) {
      try {
        await fetch('/api/notifications/device-token', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: cached })
        });
      } catch (e) { /* sem SW/FCM: segue degradado */ }
    }
  },

  async syncNative() {
    // Silencioso: nunca pede permissão aqui; só reforça vínculo se já concedida.
    try {
      const perm = await PushNotifications.checkPermissions();
      if (!perm || perm.receive !== 'granted') return;
      const cached = localStorage.getItem(FCM_TOKEN_KEY);
      if (!cached) return;
      await fetch('/api/notifications/device-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: cached, platform: Capacitor.getPlatform() })
      }).catch(() => {});
    } catch (e) {
      console.warn('FCM sync skipped:', e);
    }
  },

  async sync() {
    if (isNative()) return this.syncNative();
    // Web FCM silencioso: só reforça se já permitido.
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const token = await getFCMToken();
      localStorage.setItem(WEB_FCM_TOKEN_KEY, token);
      await fetch('/api/notifications/device-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, platform: 'web' })
      }).catch(() => {});
    } catch (e) {
      console.error('Error syncing push subscription:', e);
    }
  }
};

export default pushService;
